import { supabase } from '../../lib/supabase'
import { isRetryableSyncError } from './syncPolicy'

export interface CatalogCard {
  id: number
  name: string
  category_name: string
  image_path: string | null
}

interface InventoryRow {
  card_id: number
  owned_count: number
  wanted_count: number
  keep_count: number
  level: number
  updated_at: string
}

export async function loadCards(gameAccountId: string, locale = 'en') {
  const [catalog, inventory] = await Promise.all([
    supabase.rpc('card_catalog_list', { p_locale: locale }),
    supabase.rpc('card_inventory_list', { p_game_account_id: gameAccountId }),
  ])
  if (catalog.error) throw catalog.error
  if (inventory.error) throw inventory.error
  const counts = new Map(
    (inventory.data as InventoryRow[] | null)?.map((row) => [row.card_id, row]),
  )
  return ((catalog.data ?? []) as CatalogCard[]).map((card) => {
    const row = counts.get(card.id)
    return {
      id: card.id,
      name: card.name,
      category: card.category_name,
      imagePath: card.image_path,
      owned: row?.owned_count ?? 0,
      wanted: row?.wanted_count ?? 0,
      keep: row?.keep_count ?? 1,
      level: row?.level ?? 1,
      updatedAt: row?.updated_at,
    }
  })
}

export async function saveCard(
  gameAccountId: string,
  card: { id: number; owned: number; wanted: number; keep: number; level: number },
) {
  const { error } = await supabase.rpc('card_inventory_upsert', {
    p_game_account_id: gameAccountId,
    p_card_id: card.id,
    p_owned_count: card.owned,
    p_wanted_count: card.wanted,
    p_keep_count: card.keep,
    p_level: card.level,
  })
  if (error) throw error
}

const QUEUE_KEY = 'clash_cards_inventory_queue_v1'
type QueueItem = {
  userId?: string
  gameAccountId: string
  card: { id: number; owned: number; wanted: number; keep: number; level: number }
}
const saveChains = new Map<string, Promise<'saved' | 'queued'>>()

export function readInventoryQueue(storage: Pick<Storage, 'getItem'> = localStorage): QueueItem[] {
  try {
    const value = JSON.parse(storage.getItem(QUEUE_KEY) ?? '[]')
    if (!Array.isArray(value)) return []
    return value.filter((item): item is QueueItem =>
      Boolean(
        item &&
        typeof item === 'object' &&
        typeof item.gameAccountId === 'string' &&
        (item.userId === undefined || typeof item.userId === 'string') &&
        item.card &&
        Number.isInteger(item.card.id) &&
        Number.isInteger(item.card.owned) &&
        Number.isInteger(item.card.wanted) &&
        Number.isInteger(item.card.keep) &&
        (item.card.level === undefined || Number.isInteger(item.card.level)),
      ),
    )
  } catch {
    return []
  }
}

function writeInventoryQueue(queue: QueueItem[]) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
}

export function clearInventoryQueueForUser(userId: string) {
  writeInventoryQueue(readInventoryQueue().filter((item) => item.userId !== userId))
}

async function saveCardOrQueue(
  userId: string,
  gameAccountId: string,
  card: QueueItem['card'],
): Promise<'saved' | 'queued'> {
  try {
    await saveCard(gameAccountId, card)
    return 'saved'
  } catch (error) {
    if (!isRetryableSyncError(error, navigator.onLine)) throw error
    const queue = readInventoryQueue()
    const next = queue.filter(
      (item) => !(item.gameAccountId === gameAccountId && item.card.id === card.id),
    )
    next.push({ userId, gameAccountId, card })
    writeInventoryQueue(next)
    return 'queued'
  }
}

export function saveCardWithQueue(
  userId: string,
  gameAccountId: string,
  card: QueueItem['card'],
): Promise<'saved' | 'queued'> {
  const key = `${gameAccountId}:${card.id}`
  const previous = saveChains.get(key) ?? Promise.resolve<'saved' | 'queued'>('saved')
  const current = previous
    .catch(() => undefined)
    .then(() => saveCardOrQueue(userId, gameAccountId, card))
    .finally(() => {
      if (saveChains.get(key) === current) saveChains.delete(key)
    })
  saveChains.set(key, current)
  return current
}

export async function flushInventoryQueue(userId: string) {
  const queue = readInventoryQueue()
  const failed: QueueItem[] = []
  let sent = 0
  for (const item of queue) {
    if (!item.userId || item.userId !== userId) {
      failed.push(item)
      continue
    }
    const normalized = { ...item, card: { ...item.card, level: item.card.level ?? 1 } }
    try {
      await saveCard(normalized.gameAccountId, normalized.card)
      sent++
    } catch (error) {
      if (isRetryableSyncError(error, navigator.onLine)) failed.push(normalized)
    }
  }
  writeInventoryQueue(failed)
  return { sent, remaining: failed.length }
}
